import type { SVGProps } from 'react'
import { HebrewTranslateIcon } from './hebrew-translate-icon'

interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'width' | 'height'> {
  size?: number
}

function base(props: IconProps, viewBox: string, children: React.ReactNode) {
  const { size = 24, ...rest } = props
  return (
    <svg
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      {children}
    </svg>
  )
}

export function LanguageSwitcherIcon(props: IconProps) {
  return base(
    props,
    '0 0 48 48',
    <>
      <path d="M5.50211 25.2199C6.33023 25.2199 7.00211 24.548 7.00211 23.7199C7.00211 19.7433 10.2375 16.508 14.214 16.508H31.3296V20.0126C31.3296 21.5523 32.9962 22.5146 34.3296 21.7448L42.9982 16.7399C44.3316 15.9701 44.3316 14.0456 42.9982 13.2758L34.3296 8.27091C32.9962 7.50114 31.3296 8.46342 31.3296 10.0031V13.508H14.214C8.58316 13.508 4.00211 18.089 4.00211 23.7199C4.00211 24.548 4.67398 25.2199 5.50211 25.2199Z" fill="currentColor" />
      <path d="M42.4982 22.7803C41.6701 22.7803 40.9982 23.4521 40.9982 24.2803C40.9982 28.2568 37.7629 31.4922 33.7863 31.4922H16.6704V27.9873C16.6704 26.4476 15.0037 25.4854 13.6704 26.2551L5.00175 31.26C3.66844 32.0298 3.66844 33.9543 5.00175 34.7241L13.6704 39.729C15.0037 40.4988 16.6704 39.5365 16.6704 37.9968V34.4922H33.7863C39.4172 34.4922 43.9982 29.9111 43.9982 24.2803C43.9982 23.4521 43.3263 22.7803 42.4982 22.7803Z" fill="currentColor" />
    </>,
  )
}

export function CopyIcon(props: IconProps) {
  return base(
    props,
    '0 0 36 36',
    <>
      <path d="M28.2799 4.68994V3.83984C28.2799 1.71997 26.5599 0 24.4401 0H3.82996C1.71997 0 0 1.71997 0 3.83984V24.48C0 26.5898 1.71997 28.3101 3.82996 28.3101H4.71997V11.5298C4.71997 7.75977 7.77991 4.68994 11.5499 4.68994H28.2799Z" fill="currentColor" />
      <path d="M32.16 7.68994H11.5499C9.43005 7.68994 7.71997 9.40991 7.71997 11.5298V32.1699C7.71997 34.29 9.43005 36 11.5499 36H32.16C34.2799 36 36 34.29 36 32.1699V11.5298C36 9.40991 34.2799 7.68994 32.16 7.68994Z" fill="currentColor" />
    </>,
  )
}

export function SoundIcon(props: IconProps) {
  const { size = 24, ...rest } = props
  const aspectRatio = 41 / 32
  return (
    <svg
      width={size * aspectRatio}
      height={size}
      viewBox="0 0 41 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M25.2627 27.3929C24.8301 27.3929 24.4014 27.2074 24.1045 26.848C23.5771 26.2083 23.668 25.263 24.3066 24.7357C26.9121 22.5863 28.4062 19.4017 28.4062 15.9984C28.4062 12.6078 26.9111 9.42221 24.3037 7.25814C23.666 6.72885 23.5781 5.78353 24.1074 5.14584C24.6367 4.50814 25.583 4.42123 26.2197 4.94955C29.5156 7.68588 31.4062 11.7122 31.4062 15.9984C31.4062 20.3001 29.5146 24.3285 26.2168 27.0501C25.9365 27.2806 25.5986 27.3929 25.2627 27.3929Z" fill="currentColor" />
      <path d="M32.1328 31.9952C31.7265 31.9952 31.3203 31.8302 31.0244 31.5059C30.4668 30.8936 30.5097 29.9454 31.122 29.3868C34.875 25.963 37.0273 21.0831 37.0273 15.9981C37.0273 10.9249 34.874 6.04403 31.1201 2.60654C30.5088 2.04696 30.4668 1.09775 31.0263 0.487394C31.5859 -0.12491 32.5371 -0.164949 33.1455 0.393644C37.5195 4.39853 40.0273 10.086 40.0273 15.9981C40.0273 21.9249 37.5185 27.6124 33.1435 31.6036C32.8564 31.8653 32.4941 31.9952 32.1328 31.9952Z" fill="currentColor" />
      <path d="M17.8699 0.000976562H3C1.33984 0.000976562 0 1.34094 0 3.00098V29.001C0 30.6509 1.33984 32.001 3 32.001H17.8699C19.52 32.001 20.8699 30.6509 20.8699 29.001V3.00098C20.8699 1.34094 19.52 0.000976562 17.8699 0.000976562ZM14.97 17.731L8.8999 21.231C7.56982 22.001 5.8999 21.0409 5.8999 19.501V12.491C5.8999 10.9609 7.56982 9.99097 8.8999 10.761L14.97 14.271C16.2998 15.0409 16.2998 16.9609 14.97 17.731Z" fill="currentColor" />
    </svg>
  )
}

export function StarIcon(props: IconProps) {
  return base(
    props,
    '0 0 37 36',
    <path d="M20.3483 1.24004L23.9622 10.0375C24.2496 10.7371 24.907 11.2147 25.6611 11.2718L35.1448 11.9903C36.9271 12.1254 37.6507 14.3523 36.2881 15.5092L29.038 21.6648C28.4615 22.1543 28.2104 22.9271 28.3891 23.662L30.6364 32.9036C31.0588 34.6404 29.1644 36.0167 27.6431 35.0783L19.5483 30.0853C18.9046 29.6882 18.092 29.6882 17.4483 30.0853L9.35357 35.0783C7.83225 36.0167 5.93787 34.6404 6.36022 32.9036L8.60748 23.662C8.78618 22.9271 8.53507 22.1543 7.95855 21.6648L0.70843 15.5092C-0.654148 14.3523 0.0694563 12.1254 1.8518 11.9903L11.3354 11.2718C12.0896 11.2147 12.747 10.7371 13.0344 10.0375L16.6483 1.24004C17.3275 -0.413346 19.6691 -0.413346 20.3483 1.24004Z" fill="currentColor" />,
  )
}

export function SendIcon(props: IconProps) {
  return base(
    props,
    '0 0 36 36',
    <path d="M33.2586 0.164405L1.18779 13.909C-0.400378 14.5896 -0.394565 16.8432 1.19709 17.5156L10.1533 21.2995C10.6515 21.51 11.2246 21.4343 11.6511 21.1018L20.0307 14.5693C20.9551 13.8487 22.1472 15.041 21.4266 15.9652L14.8877 24.352C14.5549 24.7788 14.4794 25.3523 14.6903 25.8507L18.4796 34.804C19.1528 36.3947 21.4053 36.3998 22.0857 34.8122L35.8319 2.73774C36.5288 1.11166 34.8847 -0.532488 33.2586 0.164405Z" fill="currentColor" />,
  )
}

export function CloseIcon(props: IconProps) {
  return base(
    props,
    '0 0 48 48',
    <path d="M26.1212 24L41.5605 8.56104C42.1465 7.9751 42.1465 7.02588 41.5605 6.43994C40.9746 5.854 40.0254 5.854 39.4395 6.43994L24 21.8789L8.56055 6.43994C7.97461 5.854 7.02539 5.854 6.43945 6.43994C5.85352 7.02588 5.85352 7.9751 6.43945 8.56104L21.8788 24L6.43945 39.439C5.85352 40.0249 5.85352 40.9741 6.43945 41.5601C6.73242 41.853 7.11621 41.9995 7.5 41.9995C7.88379 41.9995 8.26758 41.853 8.56055 41.5601L24 26.1211L39.4395 41.5601C39.7324 41.853 40.1162 41.9995 40.5 41.9995C40.8838 41.9995 41.2676 41.853 41.5605 41.5601C42.1465 40.9741 42.1465 40.0249 41.5605 39.439L26.1212 24Z" fill="currentColor" />,
  )
}

export function DeleteIcon(props: IconProps) {
  return base(
    props,
    '0 0 36 36',
    <>
      <path d="M34 5.927H29.2871C28.146 2.47876 24.8872 0 21.0681 0H14.928C11.1089 0 7.8501 2.47876 6.70898 5.927H2C0.895508 5.927 0 6.82251 0 7.927V8.7124C0 9.81689 0.895508 10.7124 2 10.7124H34C35.1047 10.7124 36 9.81689 36 8.7124V7.927C36 6.82251 35.1047 5.927 34 5.927ZM14.928 3H21.0681C23.2068 3 25.0659 4.17871 26.0361 5.927H9.95996C10.9302 4.17871 12.7893 3 14.928 3Z" fill="currentColor" />
      <path d="M31.448 13.71H4.54812C3.24807 13.71 2.29812 14.9202 2.59816 16.1799L7.05813 34.4299C7.2781 35.3301 8.07815 35.96 8.99807 35.96H27.0081C27.928 35.96 28.728 35.3301 28.948 34.4299L33.398 16.1799C33.698 14.9202 32.7481 13.71 31.448 13.71ZM13.6279 31.47C13.5781 31.48 13.5281 31.48 13.478 31.48C12.718 31.48 12.0681 30.9001 11.9881 30.1201L11.0181 19.8301C10.938 19.01 11.5381 18.28 12.3682 18.2C13.178 18.1201 13.928 18.73 13.9981 19.55L14.978 29.8401C15.0581 30.6599 14.448 31.3899 13.6279 31.47ZM24.0081 30.1201C23.938 30.9001 23.2781 31.48 22.5181 31.48C22.468 31.48 22.418 31.48 22.3779 31.47C21.5481 31.3899 20.948 30.6599 21.0181 29.8401L21.9981 19.55C22.0781 18.73 22.8081 18.1201 23.6279 18.2C24.458 18.28 25.0581 19.01 24.9881 19.8301L24.0081 30.1201Z" fill="currentColor" />
    </>,
  )
}

export function RetryIcon(props: IconProps) {
  return base(
    props,
    '0 0 37 36',
    <path d="M29.9252 4.49242L34.5784 4.31151C35.4055 4.27928 36.051 3.58202 36.0188 2.75389C35.9875 1.94628 35.3225 1.31249 34.5207 1.31249C34.5012 1.31249 34.4817 1.31249 34.4612 1.31346L25.2385 1.67186C24.4114 1.70409 23.7659 2.40135 23.7981 3.22948L24.1565 12.4521C24.1877 13.2598 24.8528 13.8935 25.6545 13.8935C25.6741 13.8935 25.6936 13.8935 25.7141 13.8926C26.5412 13.8603 27.1868 13.1631 27.1545 12.3349L26.905 5.91307C30.5469 8.59471 32.831 12.8025 33.011 17.415C33.1672 21.4209 31.7532 25.248 29.0305 28.1924C26.3069 31.1357 22.6008 32.8428 18.594 32.998C14.6077 33.1455 10.761 31.7402 7.81761 29.0176C4.87425 26.2939 3.16722 22.5879 3.01195 18.581C2.8557 14.5752 4.26976 10.748 6.99242 7.80467C9.71507 4.86131 13.4211 3.15428 17.428 2.99901C18.2551 2.96678 18.9006 2.26952 18.8684 1.44139C18.8362 0.614244 18.1789 -0.0283337 17.3108 0.000963221C12.5041 0.188463 8.05687 2.23631 4.79027 5.76756C1.52269 9.29881 -0.17262 13.8906 0.0139038 18.6982C0.201404 23.5058 2.24926 27.9521 5.78051 31.2197C9.13597 34.3242 13.4485 36.0098 17.9934 36.0098C18.2317 36.0098 18.4719 36.0049 18.7112 35.9961C23.5178 35.8086 27.9651 33.7607 31.2327 30.2295C34.4993 26.6973 36.1955 22.1055 36.009 17.2978C35.8142 12.3066 33.569 7.70726 29.9252 4.49242Z" fill="currentColor" />,
  )
}

export function PashutIcon(props: IconProps) {
  return base(
    props,
    '0 0 48 48',
    <>
      <path d="M7.96686 15.7516C7.96686 14.9216 8.63684 14.2516 9.46686 14.2516H20.7902C21.6202 14.2516 22.2902 14.9216 22.2902 15.7516C22.2902 16.5817 21.6202 17.2516 20.7902 17.2516H9.46686C8.63684 17.2516 7.96686 16.5817 7.96686 15.7516Z" fill="currentColor" />
      <path d="M4.01685 24.6458C4.01685 23.8258 4.68683 23.1458 5.51685 23.1458H14.8402C15.6702 23.1458 16.3402 23.8258 16.3402 24.6458C16.3402 25.4758 15.6702 26.1458 14.8402 26.1458H5.51685C4.68683 26.1458 4.01685 25.4758 4.01685 24.6458Z" fill="currentColor" />
      <path d="M10.7088 33.54C10.7088 32.71 11.3788 32.04 12.2088 32.04H19.5922C20.4122 32.04 21.0922 32.71 21.0922 33.54C21.0922 34.3699 20.4122 35.04 19.5922 35.04H12.2088C11.3788 35.04 10.7088 34.3699 10.7088 33.54Z" fill="currentColor" />
      <path d="M24.822 38.1369L28.8384 30.1845C29.2674 29.3351 28.6501 28.3318 27.6985 28.3318H20.3032C19.1725 28.3318 18.5996 26.9708 19.3898 26.1622L36.7536 8.39401C37.7437 7.38085 39.4012 8.49296 38.8391 9.79329L34.2643 20.3764C33.8998 21.2196 34.518 22.1602 35.4365 22.1602H42.7036C43.8697 22.1602 44.4258 23.5946 43.5643 24.3806L26.8227 39.656C25.775 40.612 24.1826 39.4029 24.822 38.1369Z" fill="currentColor" />
    </>,
  )
}

export function ChachamIcon(props: IconProps) {
  return base(
    props,
    '0 0 48 48',
    <>
      <path d="M18.508 34.9156C18.8478 35.0368 18.9348 35.4757 18.6797 35.7308L12.7402 41.6704C12.0502 42.3604 10.8702 41.8604 10.8902 40.8804L10.9302 38.1605C10.9392 37.5493 10.4414 36.5515 9.8302 37.0604L7.10016 37.1003C6.13018 37.1204 5.63018 35.9404 6.32019 35.2505L11.2688 30.2925C11.4424 30.1185 11.7128 30.1061 11.9148 30.2461C12.5942 30.7167 13.2993 31.0242 13.8802 31.2804C14.2402 31.4304 14.6502 31.6104 14.8202 31.7405C14.9902 31.8604 15.3226 32.1587 15.5302 32.4805C16.0457 33.2795 17.2866 34.48 18.508 34.9156Z" fill="currentColor" />
      <path d="M40.8902 37.1003L38.1561 37.0604C37.5456 37.0514 37.049 37.5499 37.0602 38.1604L37.1102 40.8804C37.1202 41.8604 35.9402 42.3604 35.2502 41.6704L29.2582 35.6784C28.9996 35.4197 29.1024 34.987 29.4455 34.8601C30.7894 34.3629 31.6892 33.375 32.4602 32.4805C32.7102 32.1904 33.0002 31.8604 33.1702 31.7405C33.3402 31.6104 33.7502 31.4404 34.1102 31.2804C34.6912 31.0241 35.3963 30.7166 36.0759 30.2458C36.2778 30.106 36.5479 30.1182 36.7216 30.2919L41.6802 35.2505C42.3702 35.9404 41.8702 37.1204 40.8902 37.1003Z" fill="currentColor" />
      <path d="M35.9875 15.2215C35.5575 13.9015 35.8475 12.1915 35.0375 11.0914C34.2275 9.98155 32.5175 9.73155 31.3975 8.92149C30.2975 8.12144 29.5375 6.5615 28.2175 6.13145C26.9375 5.71153 25.4075 6.51158 23.9975 6.51158C22.5775 6.51158 21.0475 5.71153 19.7775 6.13145C18.4475 6.5615 17.6875 8.12144 16.5875 8.92149C15.4675 9.73155 13.7575 9.98155 12.9475 11.0914C12.1475 12.1915 12.4375 13.9015 12.0075 15.2215C11.5875 16.5016 10.3375 17.7015 10.3375 19.1214C10.3375 20.5315 11.5875 21.7315 12.0075 23.0116C12.4375 24.3414 12.1475 26.0415 12.9475 27.1415C13.7575 28.2616 15.4675 28.5016 16.5875 29.3115C17.6875 30.1214 18.4475 31.6715 19.7775 32.1014C21.0475 32.5215 22.5775 31.7215 23.9975 31.7215C25.4075 31.7215 26.9375 32.5215 28.2175 32.1014C29.5375 31.6715 30.2975 30.1214 31.3975 29.3115C32.5175 28.5016 34.2275 28.2616 35.0375 27.1415C35.8475 26.0415 35.5575 24.3414 35.9875 23.0116C36.3975 21.7315 37.6475 20.5315 37.6475 19.1214C37.6475 17.7015 36.3975 16.5016 35.9875 15.2215ZM30.1375 18.2815L25.0975 26.1515C24.5875 26.9515 23.4075 26.9515 22.8875 26.1515L17.8575 18.2815C17.5775 17.8414 17.5775 17.2715 17.8775 16.8414L19.4375 14.5515C19.6775 14.1915 20.0875 13.9815 20.5175 13.9815H27.4675C27.9075 13.9815 28.3075 14.1915 28.5575 14.5515L30.1175 16.8414C30.4075 17.2715 30.4175 17.8414 30.1375 18.2815Z" fill="currentColor" />
    </>,
  )
}

export function SodIcon(props: IconProps) {
  return base(
    props,
    '0 0 48 48',
    <>
      <path d="M43.9257 31.7839C43.6703 30.9968 42.8241 30.5662 42.036 30.8201L34.7848 33.1724C34.1804 31.0828 32.4579 29.5625 30.4021 29.5625C28.3109 29.5625 26.5642 31.1353 25.9895 33.281H22.0104C21.4357 31.1353 19.689 29.5625 17.5978 29.5625C15.5403 29.5625 13.8166 31.0852 13.2136 33.1777L5.96475 30.8201C5.17813 30.5652 4.33145 30.9949 4.07462 31.783C3.81827 32.571 4.24893 33.4167 5.03702 33.6736L13.1979 36.3279C13.7856 38.4496 15.5216 40 17.5978 40C19.6892 40 21.436 38.427 22.0105 36.281H25.9894C26.5639 38.427 28.3107 40 30.4021 40C32.4807 40 34.2186 38.4458 34.8041 36.3201L42.9618 33.6736C43.7499 33.4177 44.1815 32.572 43.9257 31.7839Z" fill="currentColor" />
      <path d="M34.6286 19.3002V10.0033C34.6286 8.42808 32.8921 7.47105 31.5604 8.31236L24.8108 12.5768C24.1583 12.9889 23.3268 12.9889 22.6743 12.5768L15.9247 8.31236C14.593 7.47105 12.8565 8.42808 12.8565 10.0033V19.3002C9.01212 20.0857 6.55746 21.2782 6.55746 22.6139C6.55746 24.9796 14.2515 26.8974 23.7425 26.8974C33.2336 26.8974 40.9276 24.9796 40.9276 22.6139C40.9276 21.2782 38.473 20.0857 34.6286 19.3002Z" fill="currentColor" />
    </>,
  )
}

export function ChevronDownIcon(props: IconProps) {
  const { size = 24, ...rest } = props
  return (
    <svg
      width={size}
      height={(size * 22) / 44}
      viewBox="0 0 44 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      {...rest}
    >
      <path d="M21.7497 21.949C21.3239 21.949 20.8982 21.7947 20.5632 21.4851L0.563201 3.0369C-0.14676 2.38162 -0.191681 1.2742 0.463592 0.56424C1.11887 -0.148651 2.22531 -0.190643 2.93625 0.46463L21.7507 17.8191L40.5642 0.492951C41.2751 -0.161346 42.3825 -0.116424 43.0368 0.594513C43.6921 1.30545 43.6462 2.41287 42.9353 3.06717L22.9353 21.4861C22.6003 21.7947 22.1745 21.949 21.7497 21.949Z" fill="currentColor" />
    </svg>
  )
}

export function CheckIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <path
      d="M20 6L9 17L4 12"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
  )
}

export function SearchIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <>
      <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
      <path d="M21 21L16.5 16.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>,
  )
}

export function MenuIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <path d="M4 6H20M4 12H20M4 18H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />,
  )
}

export function SunIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <>
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
      <path
        d="M12 2V4M12 20V22M4.93 4.93L6.34 6.34M17.66 17.66L19.07 19.07M2 12H4M20 12H22M4.93 19.07L6.34 17.66M17.66 6.34L19.07 4.93"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </>,
  )
}

export function MoonIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <path
      d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />,
  )
}

/**
 * The Translate mark — the Hebrew letter א. Kept as a thin wrapper over the
 * single source in `./hebrew-translate-icon` so this icon set and the rail /
 * mobile tab bar can never drift back into two different Translate glyphs.
 */
export function TranslateIcon(props: IconProps) {
  const { size = 24, ...rest } = props
  return <HebrewTranslateIcon size={size} {...rest} />
}

export function HistoryIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <>
      <path
        d="M3 12a9 9 0 1 0 3-6.7L3 8"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path d="M3 3v5h5M12 7v5l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </>,
  )
}

export function SettingsIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <>
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
      <path
        d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33h0a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51h0a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82v0a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </>,
  )
}

export function SparkleIcon(props: IconProps) {
  return base(
    props,
    '0 0 24 24',
    <path
      d="M12 3v4M12 17v4M3 12h4M17 12h4M5.64 5.64l2.83 2.83M15.54 15.54l2.83 2.83M5.64 18.36l2.83-2.83M15.54 8.46l2.83-2.83"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />,
  )
}
